import { config } from '../config';

// Donation ("give") backend (Nerd.Donations). Each function returns an axios
// request config consumed by the shared fetcher (useAuth().fetcher), matching
// the other *.resource.js files. Base URL: config.core.giveServiceUrl.
const base = () => config.core.giveServiceUrl;

/* ---------------------------------------------------------------- Gift flow */

export function getNonprofits({ includeGiftOptions = false, wallets = false } = {}) {
  const params = new URLSearchParams();
  if (includeGiftOptions) params.set('includeGiftOptions', 'true');
  if (wallets) params.set('wallets', 'true');
  const qs = params.toString();
  return {
    method: 'GET',
    url: `${base()}/nonprofit${qs ? `?${qs}` : ''}`,
  };
}

export function getNonprofitBySlug(slug, { giftOptions = true, wallets = true } = {}) {
  const params = new URLSearchParams();
  if (giftOptions) params.set('giftOptions', 'true');
  if (wallets) params.set('wallets', 'true');
  const qs = params.toString();
  return {
    method: 'GET',
    url: `${base()}/nonProfit/${slug}${qs ? `?${qs}` : ''}`,
  };
}

export function getNonprofitStripeAccount(nonprofitId) {
  return {
    method: 'GET',
    url: `${base()}/public/stripe/payment/${nonprofitId}`,
  };
}

export function createStripeDonationIntent(nonprofitId, amount) {
  return {
    method: 'POST',
    url: `${base()}/public/stripe/payment`,
    data: { nonprofitId, amount, currency: 'usd' },
  };
}

// gift: { transactionId, from, to, receivedById, giftOptionSelectedId, amount,
//         usdAmountAtTransactionTime, network, type, chainId, sardineOrderId, giverEmail }
export function savePublicGift(gift) {
  return {
    method: 'POST',
    url: `${base()}/public/gift`,
    data: gift,
  };
}

export function claimGiftWithToken(giftId, email, claimToken) {
  return {
    method: 'POST',
    url: `${base()}/public/gift/claim/${giftId}/${encodeURIComponent(email)}?claimToken=${claimToken}`,
  };
}

export function getGiftById(giftId) {
  return {
    method: 'GET',
    url: `${base()}/gift/${giftId}`,
  };
}

/* ------------------------------------------------------------ Fiat on-ramp */

export function getSardineToken() {
  return { method: 'GET', url: `${base()}/sardine/get-token` };
}

export function getSardineOrderStatus(orderId) {
  return {
    method: 'GET',
    url: `${base()}/sardine/order-status?orderId=${orderId}`,
  };
}

/* --------------------------------------------------------------- Dashboard */

// filters: { startDate, endDate, received, limit, after, before, requestedMemberId }
export function listGifts({
  startDate,
  endDate,
  received = false,
  limit = 10,
  after,
  before,
  requestedMemberId,
} = {}) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  params.set('received', String(received));
  params.set('limit', String(limit));
  if (after) params.set('after', after);
  if (before) params.set('before', before);
  if (requestedMemberId) params.set('requestedMemberId', String(requestedMemberId));
  return {
    method: 'GET',
    url: `${base()}/gift?${params.toString()}`,
  };
}

export function getGiftStatistics({
  startDate,
  endDate,
  received = false,
  requestedMemberId,
} = {}) {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  params.set('received', String(received));
  if (requestedMemberId) params.set('requestedMemberId', String(requestedMemberId));
  return {
    method: 'GET',
    url: `${base()}/gift/statistics?${params.toString()}`,
  };
}

/* ------------------------------------------------------------ Tax receipts */

export function getTaxOverview() {
  return { method: 'GET', url: `${base()}/giver/tax-receipt/overview` };
}

// Returns a PDF; caller consumes as a blob.
export function getTaxReceipt(year) {
  return {
    method: 'GET',
    url: `${base()}/giver/tax-receipt/${year}`,
    responseType: 'blob',
  };
}

/* ------------------------------------------------- Nonprofit organization */

export function getOrganizationInfo() {
  return { method: 'GET', url: `${base()}/settings/organizationInfo` };
}

// formData: multipart/form-data with org fields
export function saveOrganizationInfo(formData) {
  return {
    method: 'POST',
    url: `${base()}/settings/organizationInfo?v=2`,
    data: formData,
  };
}

export function uploadOrganizationFile(fileName, formData) {
  return {
    method: 'POST',
    url: `${base()}/settings/organizationInfo/file/${fileName}`,
    data: formData,
  };
}

/* --------------------------------------------------------- Impact updates */

export function listImpactUpdates({ page = 1, limit = 9 } = {}) {
  return {
    method: 'GET',
    url: `${base()}/nonprofit/impact-update?page=${page}&limit=${limit}`,
  };
}

export function getGiverImpactUpdates() {
  return { method: 'GET', url: `${base()}/nonprofit/impact-update/giver-list` };
}

export function getImpactUpdate(updateId) {
  return {
    method: 'GET',
    url: `${base()}/nonprofit/impact-update/${updateId}`,
  };
}

export function createImpactUpdate(formData) {
  return {
    method: 'POST',
    url: `${base()}/nonprofit/impact-update`,
    data: formData,
  };
}

export function updateImpactUpdate(updateId, formData) {
  return {
    method: 'PUT',
    url: `${base()}/nonprofit/impact-update/${updateId}`,
    data: formData,
  };
}

export function deleteImpactUpdate(updateId) {
  return {
    method: 'DELETE',
    url: `${base()}/nonprofit/impact-update/${updateId}`,
  };
}

/* --------------------------------------------------- Nonprofit Stripe Connect */

export function getStripeConnectAccountStatus() {
  return { method: 'GET', url: `${base()}/stripe/onboarding` };
}

export function startStripeConnectProcess() {
  return { method: 'POST', url: `${base()}/stripe/onboarding` };
}

/* ----------------------------------------------------------------- Tokens */

export function getGiveUsdPrice() {
  return { method: 'GET', url: `${base()}/token/price` };
}
